import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  alpha,
  Fade,
  Tooltip,
  Badge,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  ButtonGroup,
  Button,
  Switch,
  FormControlLabel,
  useTheme,
  useMediaQuery,
  Skeleton,
  Zoom,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { useUser } from './UserContext';
import { 
  Search, 
  Close, 
  Security, 
  CheckCircle, 
  Cancel, 
  PersonAdd, 
  Delete, 
  Edit,
  FilterList,
  ViewList,
  ViewModule,
  Sort,
  MoreVert,
  Group,
  AdminPanelSettings,
  TrendingUp,
  Visibility,
  VisibilityOff,
  CloudDownload,
  TableChart,
  Code,
  Description,
  VpnKey
} from '@mui/icons-material';
import { UserPermissionsDialog } from './UserPermissionsDialog';
import { CreateUserDialog } from './CreateUserDialog';
import { DeleteUserDialog } from './DeleteUserDialog';
import { DatabaseAccessDialog } from './DatabaseAccessDialog';
import { EditUserDialog } from './EditUserDialog';
import { fetchUsers, User } from '../api/admin';

// 🎨 COLOR PALETTE - Unified with AdminPanel and ChatMainView
const THEME = {
  // Backgrounds - consistent with DARK_COLORS
  BG_PRIMARY: "#000000",       // Pure black (matches DARK_COLORS.PRIMARY)
  BG_SECONDARY: "#1a1a1a",     // Card background (matches DARK_COLORS.SECONDARY)
  BG_TERTIARY: "#2d2d2d",      // Hover states (matches DARK_COLORS.ACCENT)
  
  // Surfaces
  SURFACE_GLASS: "rgba(255, 255, 255, 0.03)",
  SURFACE_ELEVATED: "#121212", // (matches DARK_COLORS.SURFACE)
  
  // Text - consistent with DARK_COLORS
  TEXT_PRIMARY: "#ffffff",     // (matches DARK_COLORS.TEXT_PRIMARY)
  TEXT_SECONDARY: "#b0b0b0",   // (matches DARK_COLORS.TEXT_SECONDARY)
  TEXT_MUTED: "#808080",
  
  // Accents - keeping modern vibrant colors but adjusted
  ACCENT_PRIMARY: "#2196f3",   // Blue - matches app theme
  ACCENT_SUCCESS: "#4caf50",   // Green (matches DARK_COLORS.SUCCESS)
  ACCENT_WARNING: "#ff9800",   // Orange (matches DARK_COLORS.WARNING)
  ACCENT_ERROR: "#ff4444",     // Red (matches DARK_COLORS.ERROR)
  ACCENT_INFO: "#2196f3",      // Blue (matches DARK_COLORS.INFO)
  
  // Role Colors - using app accent colors
  ROLE_SUPER_ADMIN: "#9c27b0",  // Purple (matches DARK_COLORS.ADMIN)
  ROLE_ADMIN: "#ff9800",       // Orange - warm admin color

  ROLE_USER: "#4caf50",        // Green - success color
  
  // Status
  STATUS_ONLINE: "#4caf50",    // Green (matches DARK_COLORS.SUCCESS)
  STATUS_OFFLINE: "#666666",
  STATUS_AWAY: "#ff9800",      // Orange (matches DARK_COLORS.WARNING)
  
  // Borders
  BORDER_SUBTLE: "#333333",    // (matches DARK_COLORS.BORDER)
  BORDER_DEFAULT: "#404040",   // (matches DARK_COLORS.HOVER)
  BORDER_STRONG: "#555555",
};

type ViewMode = 'grid' | 'list';
type FilterRole = 'all' | 'super_admin' | 'admin'  | 'user';
type SortBy = 'name' | 'role' | 'lastLogin' | 'createdAt';

type AdminUsersRedesignedProps = {
  onClose?: () => void;
};

const AdminUsersRedesigned: React.FC<AdminUsersRedesignedProps> = ({ onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user: currentUser } = useUser();
  
  // State Management
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [filterRole, setFilterRole] = useState<FilterRole>('all');
  const [sortBy, setSortBy] = useState<SortBy>('name');
  const [showInactiveUsers, setShowInactiveUsers] = useState(true);
  
  // Dialog states
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [permissionsDialogOpen, setPermissionsDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [dbAccessDialogOpen, setDbAccessDialogOpen] = useState(false);
  const [userForDbAccess, setUserForDbAccess] = useState<User | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [userToEdit, setUserToEdit] = useState<User | null>(null);

  // Load users
  const loadUsers = useCallback(async () => {
    if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'superadmin' && currentUser.role !== 'super_admin')) {
      setError('Accesso negato. Solo gli amministratori possono visualizzare questa pagina.');
      setLoading(false);
      return;
    }

    try {
      const data = await fetchUsers();
      setUsers(data);
      setError(null);
    } catch (err) {
      setError('Impossibile caricare gli utenti. Riprova più tardi.');
      // Error loading users
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Filtered and sorted users
  const processedUsers = useMemo(() => {
    let filtered = users.filter(u => {
      // Search filter
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch = 
        (u.username || '').toLowerCase().includes(searchLower) ||
        (u.email || '').toLowerCase().includes(searchLower) ||
        (u.full_name || '').toLowerCase().includes(searchLower);
      
      if (!matchesSearch) return false;

      // Role filter
      if (filterRole !== 'all' && (u.role || '').toLowerCase() !== filterRole) return false;

      // Active/Inactive filter
      if (!showInactiveUsers && !u.is_active) return false;

      return true;
    });

    // Sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.full_name || a.username || '').localeCompare(b.full_name || b.username || '');
        case 'role':
          return (a.role || '').localeCompare(b.role || '');
        case 'lastLogin':
          if (!a.last_login_local) return 1;
          if (!b.last_login_local) return -1;
          return new Date(b.last_login_local).getTime() - new Date(a.last_login_local).getTime();
        case 'createdAt':
          return new Date(b.created_at || 0).getTime() - new Date(a.created_at || 0).getTime();
        default:
          return 0;
      }
    });

    return filtered;
  }, [users, searchQuery, filterRole, sortBy, showInactiveUsers]);

  // Statistics
  const stats = useMemo(() => {
    return {
      total: users.length,
      active: users.filter(u => u.is_active).length,
      super_admin: users.filter(u => u.role === 'super_admin').length,
      admin: users.filter(u => u.role === 'admin').length,
      user: users.filter(u => u.role === 'user').length,
      online: users.filter(u => u.online).length,
    };
  }, [users]);

  // Helper functions
  const isSuperAdmin = (role: string | null | undefined) => {
    if (!role) return false;
    return role === 'super_admin' || role === 'superadmin';
  };

  const getRoleColor = (role: string | null | undefined) => {
    if (!role) return THEME.TEXT_SECONDARY;
    switch (role.toLowerCase()) {
      case 'super_admin':
      case 'superadmin': 
        return THEME.ROLE_SUPER_ADMIN;
      case 'admin': return THEME.ROLE_ADMIN;
      case 'user': return THEME.ROLE_USER;
      default: return THEME.TEXT_SECONDARY;
    }
  };

  const getRoleLabel = (role: string | null | undefined) => {
    if (!role) return 'Sconosciuto';
    switch (role.toLowerCase()) {
      case 'super_admin':
      case 'superadmin': 
        return 'Super Admin';
      case 'admin': return 'Admin';
      case 'user': return 'Utente';
      default: return role;
    }
  };

  const formatLastLogin = (lastLogin: string | null) => {
    if (!lastLogin) return 'Mai connesso';
    const date = new Date(lastLogin);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Adesso';
    if (diffMins < 60) return `${diffMins} min fa`;
    if (diffHours < 24) return `${diffHours} ore fa`;
    if (diffDays < 7) return `${diffDays} giorni fa`;
    
    return date.toLocaleDateString('it-IT', { 
      day: 'numeric', 
      month: 'short', 
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined 
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // 📥 EXPORT FUNCTIONS
  const exportToCSV = () => {
    const headers = ['Username', 'Email', 'Nome Completo', 'Ruolo', 'Attivo', 'Online', 'Ultimo Accesso', 'Data Creazione'];
    const csvData = processedUsers.map(u => [
      u.username,
      u.email,
      u.full_name || '',
      getRoleLabel(u.role),
      u.is_active ? 'Sì' : 'No',
      u.online ? 'Sì' : 'No',
      u.last_login_local ? new Date(u.last_login_local).toLocaleString('it-IT') : 'Mai',
      new Date(u.created_at).toLocaleString('it-IT')
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `utenti_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  const exportToJSON = () => {
    const exportData = processedUsers.map(u => ({
      username: u.username,
      email: u.email,
      full_name: u.full_name,
      role: u.role,
      is_active: u.is_active,
      online: u.online,
      last_login_local: u.last_login_local,
      created_at: u.created_at
    }));

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `utenti_${new Date().toISOString().split('T')[0]}.json`;
    link.click();
  };

  const exportToExcel = () => {
    // Simple Excel-compatible format (HTML table)
    const headers = ['Username', 'Email', 'Nome Completo', 'Ruolo', 'Attivo', 'Online', 'Ultimo Accesso', 'Data Creazione'];
    const rows = processedUsers.map(u => [
      u.username,
      u.email,
      u.full_name || '',
      getRoleLabel(u.role),
      u.is_active ? 'Sì' : 'No',
      u.online ? 'Sì' : 'No',
      u.last_login_local ? new Date(u.last_login_local).toLocaleString('it-IT') : 'Mai',
      new Date(u.created_at).toLocaleString('it-IT')
    ]);

    let excelContent = '<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">';
    excelContent += '<head><meta charset="UTF-8"></head><body>';
    excelContent += '<table border="1"><thead><tr>';
    headers.forEach(h => excelContent += `<th>${h}</th>`);
    excelContent += '</tr></thead><tbody>';
    rows.forEach(row => {
      excelContent += '<tr>';
      row.forEach(cell => excelContent += `<td>${cell}</td>`);
      excelContent += '</tr>';
    });
    excelContent += '</tbody></table></body></html>';

    const blob = new Blob([excelContent], { type: 'application/vnd.ms-excel' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `utenti_${new Date().toISOString().split('T')[0]}.xls`;
    link.click();
  };

  // Loading State
  if (loading) {
    return (
      <Box sx={{ 
        bgcolor: THEME.BG_PRIMARY,
        minHeight: '100vh',
        p: 3
      }}>
        <Box sx={{ mb: 4 }}>
          <Skeleton variant="text" width="40%" height={50} sx={{ bgcolor: THEME.SURFACE_GLASS }} />
          <Skeleton variant="text" width="20%" height={30} sx={{ bgcolor: THEME.SURFACE_GLASS, mt: 1 }} />
        </Box>
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton 
                variant="rectangular" 
                height={200} 
                sx={{ bgcolor: THEME.SURFACE_GLASS, borderRadius: 2 }} 
              />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  // Error State
  if (error) {
    return (
      <Box sx={{ 
        bgcolor: THEME.BG_PRIMARY,
        minHeight: '100vh',
        p: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Alert 
          severity="error"
          sx={{ 
            bgcolor: alpha(THEME.ACCENT_ERROR, 0.1),
            color: THEME.TEXT_PRIMARY,
            border: `1px solid ${alpha(THEME.ACCENT_ERROR, 0.3)}`,
            maxWidth: 600
          }}
        >
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      bgcolor: THEME.BG_PRIMARY,
      minHeight: '100vh',
      p: { xs: 2, md: 4 }
    }}>
      {/* 🎯 HEADER SECTION */}
      <Fade in timeout={300}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'flex-start',
            mb: 3,
            flexWrap: 'wrap',
            gap: 2
          }}>
            <Box>
              <Typography 
                variant="h3" 
                sx={{ 
                  color: THEME.TEXT_PRIMARY,
                  fontWeight: 800,
                  letterSpacing: '-0.03em',
                  mb: 0.5,
                  background: `linear-gradient(135deg, ${THEME.TEXT_PRIMARY} 0%, ${THEME.ACCENT_PRIMARY} 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  fontSize: { xs: '2rem', md: '3rem' }
                }}
              >
                Gestione Utenti
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                <Typography variant="body2" sx={{ color: THEME.TEXT_SECONDARY }}>
                  {stats.total} utenti totali • {stats.active} attivi
                </Typography>
                <Chip
                  icon={<TrendingUp sx={{ fontSize: 16 }} />}
                  label={`${stats.online} online`}
                  size="small"
                  sx={{
                    bgcolor: alpha(THEME.STATUS_ONLINE, 0.1),
                    color: THEME.STATUS_ONLINE,
                    border: `1px solid ${alpha(THEME.STATUS_ONLINE, 0.3)}`,
                    fontWeight: 600
                  }}
                />
              </Box>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Tooltip title="Esporta dati utenti" arrow>
                <IconButton
                  onClick={(e) => setExportMenuAnchor(e.currentTarget)}
                  sx={{
                    bgcolor: alpha(THEME.ACCENT_INFO, 0.15),
                    color: THEME.ACCENT_INFO,
                    backdropFilter: 'blur(10px)',
                    border: `1px solid ${alpha(THEME.ACCENT_INFO, 0.2)}`,
                    transition: 'all 0.2s',
                    '&:hover': {
                      bgcolor: alpha(THEME.ACCENT_INFO, 0.25),
                      transform: 'scale(1.05)',
                      boxShadow: `0 4px 16px ${alpha(THEME.ACCENT_INFO, 0.3)}`
                    }
                  }}
                >
                  <CloudDownload />
                </IconButton>
              </Tooltip>
              
              <Tooltip title="Crea nuovo utente" arrow>
                <IconButton
                  onClick={() => setCreateDialogOpen(true)}
                  sx={{
                    bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.15),
                    color: THEME.ACCENT_SUCCESS,
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.25),
                      transform: 'scale(1.05)'
                    },
                    transition: 'all 0.2s'
                  }}
                >
                  <PersonAdd />
                </IconButton>
              </Tooltip>
              
              {onClose && (
                <IconButton 
                  onClick={onClose}
                  sx={{ 
                    color: THEME.TEXT_SECONDARY,
                    '&:hover': { 
                      bgcolor: alpha(THEME.ACCENT_ERROR, 0.1),
                      color: THEME.ACCENT_ERROR
                    }
                  }}
                >
                  <Close />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* 📊 STATISTICS CARDS */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {[
              { label: 'Super Admins', value: stats.super_admin, color: THEME.ROLE_SUPER_ADMIN, icon: AdminPanelSettings },
              { label: 'Admins', value: stats.admin, color: THEME.ROLE_ADMIN, icon: Security },
              { label: 'Users', value: stats.user, color: THEME.ROLE_USER, icon: Group }
            ].map((stat, index) => (
              <Grid item xs={6} sm={3} key={stat.label}>
                <Zoom in timeout={300 + index * 100}>
                  <Card
                    sx={{
                      bgcolor: THEME.BG_SECONDARY,
                      border: `1px solid ${THEME.BORDER_SUBTLE}`,
                      borderRadius: 2,
                      overflow: 'hidden',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: 3,
                        bgcolor: stat.color
                      }
                    }}
                  >
                    <CardContent sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            bgcolor: alpha(stat.color, 0.1),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <stat.icon sx={{ color: stat.color, fontSize: 20 }} />
                        </Box>
                        <Box>
                          <Typography 
                            variant="h5" 
                            sx={{ 
                              color: THEME.TEXT_PRIMARY, 
                              fontWeight: 700,
                              lineHeight: 1 
                            }}
                          >
                            {stat.value}
                          </Typography>
                          <Typography 
                            variant="caption" 
                            sx={{ 
                              color: THEME.TEXT_MUTED,
                              fontSize: '0.7rem'
                            }}
                          >
                            {stat.label}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Zoom>
              </Grid>
            ))}
          </Grid>

          {/* 🔍 SEARCH & FILTERS BAR */}
          <Box
            sx={{
              bgcolor: THEME.BG_SECONDARY,
              border: `1px solid ${THEME.BORDER_SUBTLE}`,
              borderRadius: 2,
              p: 2,
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              alignItems: 'center',
              flexWrap: 'wrap'
            }}>
              <TextField
                placeholder="Cerca utente..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  flex: { xs: '1 1 100%', md: 1 },
                  '& .MuiOutlinedInput-root': {
                    bgcolor: THEME.SURFACE_GLASS,
                    color: THEME.TEXT_PRIMARY,
                    '& fieldset': { borderColor: THEME.BORDER_SUBTLE },
                    '&:hover fieldset': { borderColor: THEME.BORDER_DEFAULT },
                    '&.Mui-focused fieldset': { borderColor: THEME.ACCENT_PRIMARY }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: THEME.TEXT_MUTED }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton 
                        size="small" 
                        onClick={() => setSearchQuery('')}
                        sx={{ color: THEME.TEXT_MUTED }}
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />

              <ButtonGroup variant="outlined" size="small">
                <Button
                  onClick={() => setViewMode('grid')}
                  sx={{
                    bgcolor: viewMode === 'grid' ? alpha(THEME.ACCENT_PRIMARY, 0.1) : 'transparent',
                    color: viewMode === 'grid' ? THEME.ACCENT_PRIMARY : THEME.TEXT_SECONDARY,
                    borderColor: THEME.BORDER_SUBTLE
                  }}
                >
                  <ViewModule fontSize="small" />
                </Button>
                <Button
                  onClick={() => setViewMode('list')}
                  sx={{
                    bgcolor: viewMode === 'list' ? alpha(THEME.ACCENT_PRIMARY, 0.1) : 'transparent',
                    color: viewMode === 'list' ? THEME.ACCENT_PRIMARY : THEME.TEXT_SECONDARY,
                    borderColor: THEME.BORDER_SUBTLE
                  }}
                >
                  <ViewList fontSize="small" />
                </Button>
              </ButtonGroup>

              <IconButton
                onClick={() => setShowFilters(!showFilters)}
                sx={{
                  bgcolor: showFilters ? alpha(THEME.ACCENT_PRIMARY, 0.1) : THEME.SURFACE_GLASS,
                  color: showFilters ? THEME.ACCENT_PRIMARY : THEME.TEXT_SECONDARY,
                  '&:hover': {
                    bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.15)
                  }
                }}
              >
                <FilterList />
              </IconButton>
            </Box>

            {/* EXPANDED FILTERS */}
            <Collapse in={showFilters}>
              <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${THEME.BORDER_SUBTLE}` }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: THEME.TEXT_MUTED, mb: 1, display: 'block' }}>
                      Filtra per ruolo
                    </Typography>
                    <Tabs
                      value={filterRole}
                      onChange={(_, newValue) => setFilterRole(newValue)}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{
                        '& .MuiTab-root': {
                          color: THEME.TEXT_SECONDARY,
                          minHeight: 36,
                          fontSize: '0.75rem',
                          '&.Mui-selected': {
                            color: THEME.ACCENT_PRIMARY
                          }
                        },
                        '& .MuiTabs-indicator': {
                          bgcolor: THEME.ACCENT_PRIMARY
                        }
                      }}
                    >
                      <Tab label="Tutti" value="all" />
                      <Tab label="Super Admin" value="super_admin" />
                      <Tab label="Admin" value="admin" />

                      <Tab label="User" value="user" />
                    </Tabs>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: THEME.TEXT_MUTED, mb: 1, display: 'block' }}>
                      Ordina per
                    </Typography>
                    <ButtonGroup size="small" fullWidth>
                      {[
                        { value: 'name', label: 'Nome' },
                        { value: 'role', label: 'Ruolo' },
                        { value: 'lastLogin', label: 'Accesso' }
                      ].map((sort) => (
                        <Button
                          key={sort.value}
                          onClick={() => setSortBy(sort.value as SortBy)}
                          sx={{
                            bgcolor: sortBy === sort.value ? alpha(THEME.ACCENT_PRIMARY, 0.1) : 'transparent',
                            color: sortBy === sort.value ? THEME.ACCENT_PRIMARY : THEME.TEXT_SECONDARY,
                            borderColor: THEME.BORDER_SUBTLE,
                            fontSize: '0.75rem'
                          }}
                        >
                          {sort.label}
                        </Button>
                      ))}
                    </ButtonGroup>
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <Typography variant="caption" sx={{ color: THEME.TEXT_MUTED, mb: 1, display: 'block' }}>
                      Visualizza
                    </Typography>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={showInactiveUsers}
                          onChange={(e) => setShowInactiveUsers(e.target.checked)}
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: THEME.ACCENT_SUCCESS
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.5)
                            }
                          }}
                        />
                      }
                      label={
                        <Typography variant="caption" sx={{ color: THEME.TEXT_SECONDARY }}>
                          Mostra utenti inattivi
                        </Typography>
                      }
                    />
                  </Grid>
                </Grid>
              </Box>
            </Collapse>
          </Box>
        </Box>
      </Fade>

      {/* 👥 USERS GRID/LIST */}
      {viewMode === 'grid' ? (
        <Grid container spacing={3}>
          {processedUsers.map((user, index) => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={user.id}>
              <Zoom in timeout={300 + index * 50}>
                <Card
                  sx={{
                    bgcolor: THEME.BG_SECONDARY,
                    border: `1px solid ${THEME.BORDER_SUBTLE}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer',
                    position: 'relative',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: `0 12px 24px ${alpha(getRoleColor(user.role), 0.2)}`,
                      borderColor: alpha(getRoleColor(user.role), 0.5),
                      '& .action-buttons': {
                        opacity: 1,
                        transform: 'translateY(0)'
                      }
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      height: 3,
                      bgcolor: getRoleColor(user.role),
                      opacity: 0.8
                    }
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    {/* User Header */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
                      <Badge
                        overlap="circular"
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        badgeContent={
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              bgcolor: user.online ? THEME.STATUS_ONLINE : THEME.STATUS_OFFLINE,
                              border: `2px solid ${THEME.BG_SECONDARY}`,
                              boxShadow: user.online ? `0 0 8px ${THEME.STATUS_ONLINE}` : 'none'
                            }}
                          />
                        }
                      >
                        <Avatar
                          sx={{
                            width: 56,
                            height: 56,
                            bgcolor: alpha(getRoleColor(user.role), 0.2),
                            color: getRoleColor(user.role),
                            fontWeight: 700,
                            fontSize: '1.2rem',
                            border: `2px solid ${alpha(getRoleColor(user.role), 0.3)}`
                          }}
                        >
                          {getInitials(user.full_name || user.username)}
                        </Avatar>
                      </Badge>

                      <Box sx={{ ml: 'auto' }}>
                        {user.is_active ? (
                          <CheckCircle sx={{ color: THEME.ACCENT_SUCCESS, fontSize: 20 }} />
                        ) : (
                          <Cancel sx={{ color: THEME.ACCENT_ERROR, fontSize: 20 }} />
                        )}
                      </Box>
                    </Box>

                    {/* User Info */}
                    <Typography
                      variant="h6"
                      sx={{
                        color: THEME.TEXT_PRIMARY,
                        fontWeight: 700,
                        mb: 0.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {user.full_name || user.username}
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{
                        color: THEME.TEXT_MUTED,
                        mb: 1.5,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {user.email}
                    </Typography>

                    <Chip
                      label={getRoleLabel(user.role)}
                      size="small"
                      sx={{
                        bgcolor: alpha(getRoleColor(user.role), 0.1),
                        color: getRoleColor(user.role),
                        border: `1px solid ${alpha(getRoleColor(user.role), 0.3)}`,
                        fontWeight: 600,
                        fontSize: '0.7rem',
                        height: 24,
                        mb: 2
                      }}
                    />

                    <Typography
                      variant="caption"
                      sx={{
                        color: THEME.TEXT_MUTED,
                        display: 'block',
                        mb: 2
                      }}
                    >
                      Ultimo accesso: {formatLastLogin(user.last_login_local)}
                    </Typography>

                    {/* Action Buttons */}
                    <Box
                      className="action-buttons"
                      sx={{
                        display: 'flex',
                        gap: 1,
                        opacity: isMobile ? 1 : 0,
                        transform: isMobile ? 'translateY(0)' : 'translateY(8px)',
                        transition: 'all 0.3s'
                      }}
                    >
                      <Tooltip title="Permessi" arrow placement="top">
                        <IconButton
                          size="medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedUser(user);
                            setPermissionsDialogOpen(true);
                          }}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: THEME.BG_TERTIARY,
                            color: THEME.ACCENT_INFO,
                            border: `1px solid ${alpha(THEME.ACCENT_INFO, 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: alpha(THEME.ACCENT_INFO, 0.15),
                              borderColor: THEME.ACCENT_INFO,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${alpha(THEME.ACCENT_INFO, 0.3)}`
                            }
                          }}
                        >
                          <Security fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Accesso Database" arrow placement="top">
                        <IconButton
                          size="medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserForDbAccess(user);
                            setDbAccessDialogOpen(true);
                          }}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: THEME.BG_TERTIARY,
                            color: '#9c27b0',
                            border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: alpha('#9c27b0', 0.15),
                              borderColor: '#9c27b0',
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${alpha('#9c27b0', 0.3)}`
                            }
                          }}
                        >
                          <VpnKey fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Modifica" arrow placement="top">
                        <IconButton
                          size="medium"
                          onClick={(e) => {
                            e.stopPropagation();
                            setUserToEdit(user);
                            setEditDialogOpen(true);
                          }}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: THEME.BG_TERTIARY,
                            color: THEME.ACCENT_WARNING,
                            border: `1px solid ${alpha(THEME.ACCENT_WARNING, 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: alpha(THEME.ACCENT_WARNING, 0.15),
                              borderColor: THEME.ACCENT_WARNING,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${alpha(THEME.ACCENT_WARNING, 0.3)}`
                            }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip 
                        title={isSuperAdmin(user.role) ? "Super Admin non può essere eliminato" : "Elimina"} 
                        arrow 
                        placement="top"
                      >
                        <span>
                          <IconButton
                            size="medium"
                            disabled={isSuperAdmin(user.role)}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!isSuperAdmin(user.role)) {
                                setUserToDelete(user);
                                setDeleteDialogOpen(true);
                              }
                            }}
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: THEME.BG_TERTIARY,
                              color: isSuperAdmin(user.role) ? THEME.TEXT_MUTED : THEME.ACCENT_ERROR,
                              border: `1px solid ${alpha(isSuperAdmin(user.role) ? THEME.TEXT_MUTED : THEME.ACCENT_ERROR, 0.2)}`,
                              opacity: isSuperAdmin(user.role) ? 0.5 : 1,
                              cursor: isSuperAdmin(user.role) ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': !isSuperAdmin(user.role) ? {
                                bgcolor: alpha(THEME.ACCENT_ERROR, 0.15),
                                borderColor: THEME.ACCENT_ERROR,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${alpha(THEME.ACCENT_ERROR, 0.3)}`
                              } : {}
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </CardContent>
                </Card>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      ) : (
        // LIST VIEW
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {processedUsers.map((user, index) => (
            <Fade in timeout={300 + index * 30} key={user.id}>
              <Card
                sx={{
                  bgcolor: THEME.BG_SECONDARY,
                  border: `1px solid ${THEME.BORDER_SUBTLE}`,
                  borderRadius: 2,
                  overflow: 'hidden',
                  transition: 'all 0.2s',
                  '&:hover': {
                    borderColor: alpha(getRoleColor(user.role), 0.5),
                    boxShadow: `0 4px 12px ${alpha(getRoleColor(user.role), 0.15)}`
                  }
                }}
              >
                <CardContent sx={{ p: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    {/* Avatar */}
                    <Badge
                      overlap="circular"
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                      badgeContent={
                        <Box
                          sx={{
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            bgcolor: user.online ? THEME.STATUS_ONLINE : THEME.STATUS_OFFLINE,
                            border: `2px solid ${THEME.BG_SECONDARY}`
                          }}
                        />
                      }
                    >
                      <Avatar
                        sx={{
                          width: 48,
                          height: 48,
                          bgcolor: alpha(getRoleColor(user.role), 0.2),
                          color: getRoleColor(user.role),
                          fontWeight: 700
                        }}
                      >
                        {getInitials(user.full_name || user.username)}
                      </Avatar>
                    </Badge>

                    {/* Info */}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="body1"
                          sx={{
                            color: THEME.TEXT_PRIMARY,
                            fontWeight: 600,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {user.full_name || user.username}
                        </Typography>
                        <Chip
                          label={getRoleLabel(user.role)}
                          size="small"
                          sx={{
                            bgcolor: alpha(getRoleColor(user.role), 0.1),
                            color: getRoleColor(user.role),
                            border: `1px solid ${alpha(getRoleColor(user.role), 0.3)}`,
                            height: 20,
                            fontSize: '0.65rem',
                            fontWeight: 600
                          }}
                        />
                        {user.is_active ? (
                          <CheckCircle sx={{ color: THEME.ACCENT_SUCCESS, fontSize: 16 }} />
                        ) : (
                          <Cancel sx={{ color: THEME.ACCENT_ERROR, fontSize: 16 }} />
                        )}
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: THEME.TEXT_MUTED,
                          fontSize: '0.75rem',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {user.email} • {formatLastLogin(user.last_login_local)}
                      </Typography>
                    </Box>

                    {/* Actions */}
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Permessi" arrow placement="top">
                        <IconButton
                          size="medium"
                          onClick={() => {
                            setSelectedUser(user);
                            setPermissionsDialogOpen(true);
                          }}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: THEME.BG_TERTIARY,
                            color: THEME.ACCENT_INFO,
                            border: `1px solid ${alpha(THEME.ACCENT_INFO, 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: alpha(THEME.ACCENT_INFO, 0.15),
                              borderColor: THEME.ACCENT_INFO,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${alpha(THEME.ACCENT_INFO, 0.3)}`
                            }
                          }}
                        >
                          <Security fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Accesso Database" arrow placement="top">
                        <IconButton
                          size="medium"
                          onClick={() => {
                            setUserForDbAccess(user);
                            setDbAccessDialogOpen(true);
                          }}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: THEME.BG_TERTIARY,
                            color: '#9c27b0',
                            border: `1px solid ${alpha('#9c27b0', 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: alpha('#9c27b0', 0.15),
                              borderColor: '#9c27b0',
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${alpha('#9c27b0', 0.3)}`
                            }
                          }}
                        >
                          <VpnKey fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title="Modifica" arrow placement="top">
                        <IconButton
                          size="medium"
                          onClick={() => {
                            setUserToEdit(user);
                            setEditDialogOpen(true);
                          }}
                          sx={{
                            width: 36,
                            height: 36,
                            bgcolor: THEME.BG_TERTIARY,
                            color: THEME.ACCENT_WARNING,
                            border: `1px solid ${alpha(THEME.ACCENT_WARNING, 0.2)}`,
                            transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: alpha(THEME.ACCENT_WARNING, 0.15),
                              borderColor: THEME.ACCENT_WARNING,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 4px 12px ${alpha(THEME.ACCENT_WARNING, 0.3)}`
                            }
                          }}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>

                      <Tooltip 
                        title={isSuperAdmin(user.role) ? "Super Admin non può essere eliminato" : "Elimina"} 
                        arrow 
                        placement="top"
                      >
                        <span>
                          <IconButton
                            size="medium"
                            disabled={isSuperAdmin(user.role)}
                            onClick={() => {
                              if (!isSuperAdmin(user.role)) {
                                setUserToDelete(user);
                                setDeleteDialogOpen(true);
                              }
                            }}
                            sx={{
                              width: 36,
                              height: 36,
                              bgcolor: THEME.BG_TERTIARY,
                              color: isSuperAdmin(user.role) ? THEME.TEXT_MUTED : THEME.ACCENT_ERROR,
                              border: `1px solid ${alpha(isSuperAdmin(user.role) ? THEME.TEXT_MUTED : THEME.ACCENT_ERROR, 0.2)}`,
                              opacity: isSuperAdmin(user.role) ? 0.5 : 1,
                              cursor: isSuperAdmin(user.role) ? 'not-allowed' : 'pointer',
                              transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                              '&:hover': !isSuperAdmin(user.role) ? {
                                bgcolor: alpha(THEME.ACCENT_ERROR, 0.15),
                                borderColor: THEME.ACCENT_ERROR,
                                transform: 'translateY(-2px)',
                                boxShadow: `0 4px 12px ${alpha(THEME.ACCENT_ERROR, 0.3)}`
                              } : {}
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Fade>
          ))}
        </Box>
      )}

      {/* No Results */}
      {processedUsers.length === 0 && (
        <Box sx={{ 
          textAlign: 'center', 
          py: 12,
          color: THEME.TEXT_SECONDARY 
        }}>
          <Typography variant="h5" sx={{ mb: 1, color: THEME.TEXT_PRIMARY }}>
            Nessun utente trovato
          </Typography>
          <Typography variant="body2">
            Prova a modificare i criteri di ricerca o i filtri
          </Typography>
        </Box>
      )}

      {/* DIALOGS */}
      {selectedUser && (
        <UserPermissionsDialog
          open={permissionsDialogOpen}
          onClose={() => {
            setPermissionsDialogOpen(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          onSuccess={() => {
            setPermissionsDialogOpen(false);
            setSelectedUser(null);
            loadUsers();
          }}
        />
      )}

      <CreateUserDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          setCreateDialogOpen(false);
          loadUsers();
        }}
      />

      <DeleteUserDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
        }}
        user={userToDelete}
        onSuccess={() => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
          loadUsers();
        }}
      />

      {/* EXPORT MENU */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={() => setExportMenuAnchor(null)}
        PaperProps={{
          sx: {
            bgcolor: THEME.BG_SECONDARY,
            border: `1px solid ${THEME.BORDER_SUBTLE}`,
            borderRadius: 2,
            mt: 1,
            minWidth: 220,
            boxShadow: `0 8px 32px ${alpha('#000', 0.4)}`
          }
        }}
      >
        <MenuItem
          onClick={() => {
            exportToCSV();
            setExportMenuAnchor(null);
          }}
          sx={{
            py: 1.5,
            px: 2,
            color: THEME.TEXT_PRIMARY,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: alpha(THEME.ACCENT_SUCCESS, 0.15),
              transform: 'translateX(4px)'
            }
          }}
        >
          <ListItemIcon>
            <TableChart sx={{ color: THEME.ACCENT_SUCCESS, fontSize: 24 }} />
          </ListItemIcon>
          <ListItemText 
            primary="File Excel-compatibile" 
            secondary="CSV per Excel e Fogli Google"
            primaryTypographyProps={{
              sx: { fontWeight: 600, fontSize: '0.95rem' }
            }}
            secondaryTypographyProps={{
              sx: { color: THEME.TEXT_MUTED, fontSize: '0.75rem', mt: 0.3 }
            }}
          />
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            exportToJSON();
            setExportMenuAnchor(null);
          }}
          sx={{
            py: 1.5,
            px: 2,
            color: THEME.TEXT_PRIMARY,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: alpha(THEME.ACCENT_PRIMARY, 0.15),
              transform: 'translateX(4px)'
            }
          }}
        >
          <ListItemIcon>
            <Code sx={{ color: THEME.ACCENT_PRIMARY, fontSize: 24 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Dati strutturati" 
            secondary="JSON per sviluppatori"
            primaryTypographyProps={{
              sx: { fontWeight: 600, fontSize: '0.95rem' }
            }}
            secondaryTypographyProps={{
              sx: { color: THEME.TEXT_MUTED, fontSize: '0.75rem', mt: 0.3 }
            }}
          />
        </MenuItem>
        
        <MenuItem
          onClick={() => {
            exportToExcel();
            setExportMenuAnchor(null);
          }}
          sx={{
            py: 1.5,
            px: 2,
            color: THEME.TEXT_PRIMARY,
            transition: 'all 0.2s',
            '&:hover': {
              bgcolor: alpha(THEME.ACCENT_WARNING, 0.15),
              transform: 'translateX(4px)'
            }
          }}
        >
          <ListItemIcon>
            <Description sx={{ color: THEME.ACCENT_WARNING, fontSize: 24 }} />
          </ListItemIcon>
          <ListItemText 
            primary="Formato Microsoft Excel" 
            secondary="XLS per Microsoft Office"
            primaryTypographyProps={{
              sx: { fontWeight: 600, fontSize: '0.95rem' }
            }}
            secondaryTypographyProps={{
              sx: { color: THEME.TEXT_MUTED, fontSize: '0.75rem', mt: 0.3 }
            }}
          />
        </MenuItem>
      </Menu>

      {/* Database Access Dialog */}
      {userForDbAccess && (
        <DatabaseAccessDialog
          open={dbAccessDialogOpen}
          onClose={() => {
            setDbAccessDialogOpen(false);
            setUserForDbAccess(null);
          }}
          user={userForDbAccess}
        />
      )}

      {/* Edit User Dialog */}
      {userToEdit && (
        <EditUserDialog
          open={editDialogOpen}
          onClose={() => {
            setEditDialogOpen(false);
            setUserToEdit(null);
          }}
          user={userToEdit}
          onSuccess={() => {
            setEditDialogOpen(false);
            setUserToEdit(null);
            loadUsers();
          }}
          isSuperAdmin={currentUser?.role === 'super_admin' || currentUser?.role === 'superadmin'}
        />
      )}
    </Box>
  );
};

export default AdminUsersRedesigned;
